/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DownloadIcon from "@material-ui/icons/GetApp";

import { getDeviceLogs } from "../../services/dashboardDataProvider";
import { dayjs } from "utils/time.jsx";
import {
  PERMISSIONS_ADMIN,
  CAN_SEE_DEVICE_LOGS
} from "utils/userPermissions.jsx";
import { isDarkUiTheme } from "theme/deviceDetailsTheme.js";
import { getAppShellUi } from "theme/appShellTheme.js";

const styles = theme => {
  const isDark = isDarkUiTheme(theme);
  const ui = getAppShellUi(isDark);

  return {
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0"
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF"
    }
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1"
    }
  },
  cardIconTitle: {
    color: ui.textMuted,
    margin: "10px 0 0",
    fontWeight: "300"
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  CardBody: {
    // padding: "2px"
  },
  alarmsContainer: {
    // padding: "2px"
  },
  deviceLogActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "12px",
    alignItems: "flex-end",
    padding: "12px",
    border: `1px solid ${ui.border}`,
    borderRadius: "10px",
    background: ui.surfaceRaised
  },
  downloadButton: {
    height: "40px",
    marginBottom: "2px",
    borderRadius: "8px",
    fontWeight: 600,
    letterSpacing: "0.2px",
    textTransform: "none"
  },
  downloadIcon: {
    fontSize: "18px",
    marginRight: "6px"
  },
  logsInfoRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    marginBottom: "10px"
  },
  deviceLogs: {
    backgroundColor: "#0f172a",
    backgroundImage:
      "linear-gradient(180deg, rgba(30,41,59,1) 0%, rgba(15,23,42,1) 100%)",
    padding: "10px",
    borderRadius: "10px",
    fontFamily: "Menlo, Monaco, Consolas, monospace",
    fontSize: "13px",
    maxHeight: "600px",
    overflowY: "auto",
    border: "1px solid #1e293b"
  },
  logRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    padding: "6px 8px",
    borderRadius: "6px",
    color: "#e2e8f0",
    marginBottom: "4px"
  },
  logRowAlt: {
    backgroundColor: "rgba(255,255,255,0.03)"
  },
  logIp: {
    color: "#60a5fa",
    fontWeight: 700,
    whiteSpace: "nowrap"
  },
  logLevel: {
    fontWeight: 700,
    whiteSpace: "nowrap"
  },
  logTime: {
    color: "#34d399",
    fontStyle: "italic",
    whiteSpace: "nowrap"
  },
  logMessage: {
    color: "#f8fafc",
    wordBreak: "break-word"
  },
  loadingText: {
    color: "#93c5fd",
    padding: "8px"
  },
  emptyText: {
    color: "#cbd5e1",
    padding: "8px"
  }
};
};

class DeviceLogs extends React.Component {
  constructor(props) {
    super(props);
    this.deviceLogsContainerRef = React.createRef();
    this.state = {
      deviceId: null,
      deviceLogsRaw: null,
      deviceLogs: [],
      loading: false,
      logsInputData: {
        date: dayjs().format("YYYY-MM-DD"),
        logSearchPattern: ""
      }
    };
  }

  componentDidMount() {
    const deviceData = this.props.deviceData;
    if (deviceData) {
      this.setState(
        {
          deviceId: deviceData.ip_address,
          deviceData: deviceData
        },
        () => {
          // Load logs after device ID is set
          this.fetchDeviceLogs();
        }
      );
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      (prevProps.deviceData && prevProps.deviceData.ip_address) !==
      (this.props.deviceData && this.props.deviceData.ip_address)
    ) {
      const deviceData = this.props.deviceData;
      if (deviceData) {
        this.setState(
          {
            deviceId: deviceData.ip_address,
            deviceData: deviceData
          },
          () => {
            this.fetchDeviceLogs();
          }
        );
      }
    }

    if (prevState.loading && !this.state.loading) {
      this.scrollLogsToEnd();
    }
  }

  scrollLogsToEnd = () => {
    const logsContainer = this.deviceLogsContainerRef.current;
    if (logsContainer) {
      logsContainer.scrollTop = logsContainer.scrollHeight;
    }
  };

  filterLogs = logsRaw => {
    const logSearchPattern =
      this.state.logsInputData && this.state.logsInputData.logSearchPattern;
    return logsRaw.filter(
      log => !logSearchPattern || log.includes(logSearchPattern)
    );
  };

  fetchDeviceLogs = deviceId => {
    const { user } = this.props;
    const userPermissions = user.user_permissions || [];
    const userCanSeeLogs =
      userPermissions.includes(PERMISSIONS_ADMIN) ||
      userPermissions.includes(CAN_SEE_DEVICE_LOGS);
    const date = this.state.logsInputData.date || dayjs().format("YYYY-MM-DD");

    if (!userCanSeeLogs) {
      return;
    }

    this.setState({ loading: true });

    getDeviceLogs(
      this.props.axios,
      this.state.deviceId || deviceId,
      date,
      "json"
    )
      .then(data => {
        const logsRaw = data["logs"] || [];
        this.setState({
          deviceLogsRaw: logsRaw,
          deviceLogs: this.filterLogs(logsRaw),
          loading: false
        });
      })
      .catch(() => {
        this.setState({
          deviceLogsRaw: [],
          deviceLogs: [],
          loading: false
        });
      });
  };

  parseLogs = () => {
    const { classes } = this.props;
    let logsRaw = this.state.deviceLogs || [];

    const levelColors = {
      ERROR: "#f87171",
      WARNING: "#fbbf24",
      WARN: "#fbbf24",
      INFO: "#60a5fa",
      DEBUG: "#a78bfa"
    };

    return logsRaw.map((logText, idx) => {
      const logPattern = /^(?<deviceIP>[\d.]+) -> (?<logLevel>\w+) (?<time>[\d-]+ [\d:,]+) (?<logText>.+)/;
      const match = logText.match(logPattern);
      if (match) {
        const { deviceIP, logLevel, time, logText } = match.groups;
        const normalizedLogLevel = (logLevel || "").toUpperCase();
        const levelColor = levelColors[normalizedLogLevel] || "#f8fafc";

        return (
          <div
            key={idx}
            className={`${classes.logRow} ${idx % 2 === 0 ? classes.logRowAlt : ""}`}
          >
            <span className={classes.logIp}>
              {deviceIP}
            </span>
            {" ->"}
            <span className={classes.logLevel} style={{ color: levelColor }}>
              {" "}
              {logLevel}
            </span>{" "}
            <span className={classes.logTime}>
              {time}
            </span>{" "}
            <span className={classes.logMessage}>{logText}</span>
          </div>
        );
      }
      return (
        <div
          key={idx}
          className={`${classes.logRow} ${idx % 2 === 0 ? classes.logRowAlt : ""}`}
        >
          <span className={classes.logMessage}>{logText}</span>
        </div>
      );
    });
  };

  onChange = (dictName, propName) => e => {
    const value = e.target.value;
    if (dictName == null || dictName === "") {
      this.setState({ [propName]: value });
    } else {
      let updatedData = this.state[dictName] || {};
      updatedData[propName] = value;
      this.setState({ [dictName]: updatedData });
    }

    if (dictName === "logsInputData") {
      if (propName === "date") {
        // Fetch logs when date changes
        setTimeout(() => {
          this.fetchDeviceLogs();
        }, 100);
      }
      if (propName === "logSearchPattern") {
        // Filter logs when search pattern changes
        this.setState({
          deviceLogs: this.filterLogs(this.state.deviceLogsRaw)
        });
      }
    }
  };

  downloadLogs = () => {
    const element = document.createElement("a");
    const file = new Blob(this.state.deviceLogsRaw, {
      type: "text/plain"
    });
    element.href = URL.createObjectURL(file);
    element.download = `device-logs-${this.state.deviceId}-${
      this.state.logsInputData.date
    }.txt`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  render() {
    const { classes, user } = this.props;
    const { loading, logsInputData, deviceLogs } = this.state;

    const userPermissions = user.user_permissions || [];
    const userCanSeeLogs =
      userPermissions.includes(PERMISSIONS_ADMIN) ||
      userPermissions.includes(CAN_SEE_DEVICE_LOGS);

    if (!userCanSeeLogs) {
      return (
        <Card>
          <CardHeader color="warning">
            <h4 className={classes.cardTitleWhite}>Access Denied</h4>
            <p className={classes.cardCategoryWhite}>
              You don&apos;t have permission to view device logs
            </p>
          </CardHeader>
        </Card>
      );
    }

    return (
      <Card>
        <CardHeader color="info">
          <div className={classes.headerContainer}>
            <div>
              <h4 className={classes.cardTitleWhite}>Device Logs</h4>
              <p className={classes.cardCategoryWhite}>
                View and search device logs
              </p>
            </div>
          </div>
        </CardHeader>
        <CardBody className={classes.CardBody}>
          <div className={classes.alarmsContainer}>
            <div className={classes.deviceLogActions}>
              <CustomInput
                labelText="Date"
                formControlProps={{
                  style: { minWidth: "150px" }
                }}
                inputProps={{
                  type: "date",
                  value: logsInputData.date,
                  onChange: this.onChange("logsInputData", "date")
                }}
              />
              <CustomInput
                labelText="Search Pattern"
                formControlProps={{
                  style: { minWidth: "200px" }
                }}
                inputProps={{
                  placeholder: "Enter search pattern...",
                  value: logsInputData.logSearchPattern,
                  onChange: this.onChange("logsInputData", "logSearchPattern")
                }}
              />
              <Button
                size="sm"
                color="info"
                className={classes.downloadButton}
                onClick={this.downloadLogs}
                disabled={
                  !this.state.deviceLogsRaw ||
                  this.state.deviceLogsRaw.length === 0
                }
              >
                <DownloadIcon className={classes.downloadIcon} />
                Download Logs
              </Button>
            </div>

            <div className={classes.logsInfoRow}>
              <Chip
                size="small"
                color="primary"
                label={`Date: ${logsInputData.date || "-"}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Total: ${(this.state.deviceLogsRaw || []).length}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Visible: ${deviceLogs.length}`}
              />
            </div>

            <Divider style={{ marginBottom: "10px" }} />

            <div className={classes.deviceLogs} ref={this.deviceLogsContainerRef}>
              {loading ? (
                <div className={classes.loadingText}>Loading logs...</div>
              ) : deviceLogs.length > 0 ? (
                this.parseLogs()
              ) : (
                <div className={classes.emptyText}>
                  No logs found for the selected date and search criteria.
                </div>
              )}
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }
}

DeviceLogs.propTypes = {
  classes: PropTypes.object.isRequired,
  axios: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  deviceData: PropTypes.object
};

export default withStyles(styles)(DeviceLogs);
